// connectivityStore — live cellular/network connectivity signal for the EFB.
//
// Purpose: surface a "cellular degraded — using cache" badge when the app
// is flying on SW-cached data rather than live network responses. Pilot
// should never wonder if their weather / airspace / traffic data is fresh
// or stale (OAC7).
//
// State:
//   online         true when navigator.onLine is true
//   degraded       true when we're online but getting cache hits (slow cell)
//   lastOnlineAt   ISO string of last time we had full connectivity
//
// The SW stamps 'x-fyrefly-cached-at' on cached API responses (added in
// OAC1/c3dddbb). Future work: intercept that header from fetch responses
// and feed the age into this store to surface per-dataset staleness.
// For now: online/offline is enough for the cockpit chip.

import { create } from 'zustand';

interface ConnectivityState {
  /** True when navigator.onLine reports connectivity. */
  online: boolean;
  /** ISO string of last full-connectivity moment (null before first online). */
  lastOnlineAt: string | null;
}

export const useConnectivityStore = create<ConnectivityState>(() => ({
  online: typeof navigator !== 'undefined' ? navigator.onLine : true,
  lastOnlineAt: typeof navigator !== 'undefined' && navigator.onLine
    ? new Date().toISOString()
    : null,
}));

// ── Connectivity event listeners ──────────────────────────────────────────

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    useConnectivityStore.setState({
      online: true,
      lastOnlineAt: new Date().toISOString(),
    });
  });
  window.addEventListener('offline', () => {
    useConnectivityStore.setState({ online: false });
  });
}

// ── Helpers ──────────────────────────────────────────────────────────────

/** Format 'offline since HH:MM' or 'online' for display. */
export function connectivityLabel(state: ConnectivityState): string {
  if (state.online) return 'online';
  if (state.lastOnlineAt) {
    const d = new Date(state.lastOnlineAt);
    const h = d.getHours().toString().padStart(2, '0');
    const m = d.getMinutes().toString().padStart(2, '0');
    return `offline since ${h}:${m}`;
  }
  return 'offline';
}
